import React from "react";
import { Renderer, JSONUIProvider } from "@json-render/react";
import type { Spec, ComponentRegistry } from "@json-render/react";
import {
  Stack,
  Grid,
  Section,
  Card,
  Divider,
  ScoreBar,
  StatCard,
  KeyValue,
  Table,
  BulletList,
  CheckList,
  NumberedList,
  Text,
  Callout,
  BadgeComponent,
  Heading,
  DialogComponent,
  BarChartComponent,
  LineChartComponent,
  PieChartComponent,
} from "./components";
import { parsePartialJson } from "./parse-partial-json";

/**
 * Plain component registry — maps spec "type" strings to React components.
 * No catalog, no Zod, no export pipeline. The AI prompt is built in Ruby.
 */
const registry: ComponentRegistry = {
  Stack,
  Grid,
  Section,
  Card,
  Divider,
  ScoreBar,
  StatCard,
  KeyValue,
  Table,
  BulletList,
  CheckList,
  NumberedList,
  Text,
  Callout,
  Badge: BadgeComponent,
  Heading,
  Dialog: DialogComponent,
  BarChart: BarChartComponent,
  LineChart: LineChartComponent,
  PieChart: PieChartComponent,
};

export { parsePartialJson } from "./parse-partial-json";

interface SpecRendererProps {
  /** The render spec — parsed object, or a raw/partial JSON string during streaming */
  spec: Spec | string | null;
  /** The structured data from the agent — injected as initial state */
  data: Record<string, unknown>;
}

/**
 * Strip elements with unknown component types so the renderer
 * skips them instead of crashing. Also removes children refs
 * that point to stripped elements.
 */
function sanitizeSpec(spec: Spec): Spec {
  const elements = spec.elements ?? {};
  const validKeys = new Set<string>();

  // First pass: keep only elements with known types
  for (const [key, el] of Object.entries(elements)) {
    if (el?.type && registry[el.type]) {
      validKeys.add(key);
    }
  }

  // Second pass: ensure props exist, filter children refs to valid keys only
  const sanitized: Record<string, any> = {};
  for (const key of validKeys) {
    const el = { ...elements[key] };
    // Ensure props is always an object — json-render's resolveBindings crashes on null/undefined
    if (!el.props || typeof el.props !== "object") {
      el.props = {};
    }
    if (Array.isArray(el.children)) {
      el.children = el.children.filter((c: string) => validKeys.has(c));
    }
    sanitized[key] = el;
  }

  // Root must exist
  const root = validKeys.has(spec.root) ? spec.root : validKeys.values().next().value;
  if (!root) return { root: "", elements: {} };

  return { root, elements: sanitized };
}

/**
 * Resolve a spec from either a parsed object or a raw/partial JSON string.
 * During streaming, the string may be incomplete — parsePartialJson
 * closes unclosed brackets and attempts to parse what's available.
 */
function resolveSpec(input: Spec | string | null): Spec | null {
  if (!input) return null;

  if (typeof input === "object") {
    if (input.root && input.elements) return input;
    return null;
  }

  // String — try partial JSON parse
  const parsed = parsePartialJson(input);
  if (!parsed) return null;

  const root = parsed.root as string;
  const elements = parsed.elements as Record<string, any>;
  if (!root || !elements || typeof elements !== "object") return null;

  return { root, elements } as Spec;
}

/**
 * Renders structured agent output using a json-render spec and component registry.
 *
 * Accepts either a fully parsed spec or a raw JSON string (including partial
 * strings during streaming). Unknown component types and broken children
 * refs are silently skipped.
 */
export function SpecRenderer({ spec: input, data }: SpecRendererProps) {
  const spec = resolveSpec(input);

  if (!spec) {
    return (
      <p className="text-sm text-slate-400 italic text-center py-8">
        Aucun render spec disponible.
      </p>
    );
  }

  const safeSpec = sanitizeSpec(spec);

  return (
    <JSONUIProvider registry={registry} initialState={data}>
      <Renderer spec={safeSpec} registry={registry} />
    </JSONUIProvider>
  );
}

// Backward-compat alias for schema-builder consumers pending removal in Phase 2.
export { SpecRenderer as StructuredRenderer };
